import { FormEvent, useMemo, useState } from 'react';
import { Alert, Box, Button, Grid, Paper, Snackbar, TextField, Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client/react';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import {
    DELETE_KOTOBEE_BOOK_CLOUD_ID,
    DeleteKotobeeBookCloudIdResponse,
    DeleteKotobeeBookCloudIdVariables,
    GET_KOTOBEE_BOOK_CLOUD_IDS,
    GetKotobeeBookCloudIdsResponse,
    KotobeeBookCloudIdModel,
    UPSERT_KOTOBEE_BOOK_CLOUD_ID,
    UpsertKotobeeBookCloudIdResponse,
    UpsertKotobeeBookCloudIdVariables,
} from './KotobeeBookMapping.graphql';
import ConfirmDialog from '../../components/ConfirmDialog';

function parseCloudId(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) return null;

    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;

    return parsed;
}

function getGraphQLErrorMessage(error: Error): string {
    if (((error as any)?.graphQLErrors ?? []).length > 0) {
        return ((error as any)?.graphQLErrors ?? [])[0].message;
    }

    return error.message;
}

export default function KotobeeBookMapping() {
    const [bookName, setBookName] = useState('');
    const [cloudIdInput, setCloudIdInput] = useState('');
    const [selectedMappingId, setSelectedMappingId] = useState<string | null>(null);
    const [bookNameError, setBookNameError] = useState<string | null>(null);
    const [cloudIdError, setCloudIdError] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [deleteMessage, setDeleteMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<KotobeeBookCloudIdModel | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [upsertKotobeeBookCloudId, { loading }] = useMutation<UpsertKotobeeBookCloudIdResponse, UpsertKotobeeBookCloudIdVariables>(
        UPSERT_KOTOBEE_BOOK_CLOUD_ID,
    );
    const [deleteKotobeeBookCloudId] = useMutation<DeleteKotobeeBookCloudIdResponse, DeleteKotobeeBookCloudIdVariables>(DELETE_KOTOBEE_BOOK_CLOUD_ID);

    const { data, loading: loadingMappings, error: mappingsError, refetch } = useQuery<GetKotobeeBookCloudIdsResponse>(GET_KOTOBEE_BOOK_CLOUD_IDS);

    const sortedMappings = useMemo(
        () => [...(data?.getKotobeeBookCloudIds || [])].sort((a, b) => a.bookName.localeCompare(b.bookName, undefined, { sensitivity: 'base' })),
        [data],
    );

    const columns = useMemo<GridColDef<KotobeeBookCloudIdModel>[]>(
        () => [
            { field: 'bookName', headerName: 'Bok Navn', flex: 1, minWidth: 180 },
            {
                field: 'cloudId',
                headerName: 'Cloud ID',
                width: 150,
                renderCell: (params: GridRenderCellParams<KotobeeBookCloudIdModel>) => String(params.row.cloudId),
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 140,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<KotobeeBookCloudIdModel>) => (
                    <Button
                        color="error"
                        size="small"
                        startIcon={<Delete />}
                        onClick={(event) => {
                            event.stopPropagation();
                            askDelete(params.row);
                        }}
                        disabled={deletingId === params.row.id}>
                        {deletingId === params.row.id ? 'Deleting...' : 'Delete'}
                    </Button>
                ),
            },
        ],
        [deletingId],
    );

    const trimmedBookName = useMemo(() => bookName.trim(), [bookName]);
    const parsedCloudId = useMemo(() => parseCloudId(cloudIdInput), [cloudIdInput]);

    const validate = () => {
        let isValid = true;

        if (!trimmedBookName) {
            setBookNameError('Book Name is required.');
            isValid = false;
        } else {
            setBookNameError(null);
        }

        if (parsedCloudId === null) {
            setCloudIdError('Cloud ID must be a positive whole number.');
            isValid = false;
        } else {
            setCloudIdError(null);
        }

        return isValid;
    };

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (loading) return;

        setErrorMessage(null);
        setSuccessMessage(null);

        if (!validate() || parsedCloudId === null) {
            return;
        }

        try {
            const response = await upsertKotobeeBookCloudId({
                variables: {
                    input: {
                        bookName: trimmedBookName,
                        cloudId: parsedCloudId,
                    },
                },
            });

            const saved = response.data?.upsertKotobeeBookCloudId;
            if (saved) {
                setBookName(saved.bookName);
                setCloudIdInput(String(saved.cloudId));
                setSelectedMappingId(saved.id);
                setSuccessMessage(`Saved mapping for ${saved.bookName}.`);
                await refetch();
            } else {
                setSuccessMessage('Mapping saved successfully.');
                await refetch();
            }
        } catch (error) {
            setErrorMessage(getGraphQLErrorMessage(error as Error));
        }
    };

    const selectMapping = (mapping: KotobeeBookCloudIdModel) => {
        setSelectedMappingId(mapping.id);
        setBookName(mapping.bookName);
        setCloudIdInput(String(mapping.cloudId));
        setBookNameError(null);
        setCloudIdError(null);
        setErrorMessage(null);
    };

    const askDelete = (mapping: KotobeeBookCloudIdModel) => {
        if (deletingId) return;
        setDeleteCandidate(mapping);
    };

    const closeDeleteDialog = () => {
        if (deletingId) return;
        setDeleteCandidate(null);
    };

    const confirmDelete = async () => {
        if (!deleteCandidate || deletingId) return;

        setDeleteMessage(null);
        setDeletingId(deleteCandidate.id);

        try {
            const response = await deleteKotobeeBookCloudId({
                variables: { input: { id: deleteCandidate.id } },
            });

            const wasDeleted = response.data?.deleteKotobeeBookCloudId;
            await refetch();

            if (wasDeleted) {
                if (selectedMappingId === deleteCandidate.id) {
                    setSelectedMappingId(null);
                }
                setDeleteMessage({ text: `Deleted mapping for ${deleteCandidate.bookName}.`, severity: 'success' });
            } else {
                setDeleteMessage({ text: `Mapping for ${deleteCandidate.bookName} was not found. List refreshed.`, severity: 'info' });
            }
        } catch (error) {
            setDeleteMessage({ text: getGraphQLErrorMessage(error as Error), severity: 'error' });
        } finally {
            setDeletingId(null);
            setDeleteCandidate(null);
        }
    };

    const isSaveDisabled = loading;

    return (
        <Box sx={{ mt: 3 }}>
            <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Tilkobling mellom Shopify og Kotobee
                </Typography>

                <Box component="form" onSubmit={onSubmit} noValidate>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}>
                            <TextField
                                label="Navn til bok i Shopify"
                                fullWidth
                                value={bookName}
                                onChange={(event) => {
                                    setBookName(event.target.value);
                                    if (bookNameError) setBookNameError(null);
                                }}
                                error={Boolean(bookNameError)}
                                helperText={bookNameError || ' '}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}>
                            <TextField
                                label="Cloud ID i Kotobee"
                                fullWidth
                                type="number"
                                value={cloudIdInput}
                                onChange={(event) => {
                                    setCloudIdInput(event.target.value);
                                    if (cloudIdError) setCloudIdError(null);
                                }}
                                error={Boolean(cloudIdError)}
                                helperText={cloudIdError || ' '}
                                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            />
                        </Grid>

                        {errorMessage && (
                            <Grid size={12}>
                                <Alert severity="error">{errorMessage}</Alert>
                            </Grid>
                        )}

                        <Grid size={12}>
                            <Button type="submit" variant="contained" color="primary" disabled={isSaveDisabled}>
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
            <Paper elevation={1} sx={{ mt: 2, p: 2 }}>
                <Typography variant="subtitle2">Existing mappings</Typography>

                {mappingsError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {getGraphQLErrorMessage(mappingsError)}
                    </Alert>
                )}

                {!mappingsError && (
                    <Box sx={{ mt: 1 }}>
                        <DataGridPro
                            autoHeight
                            density="compact"
                            rows={sortedMappings}
                            columns={columns}
                            loading={loadingMappings}
                            localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
                            getRowId={(row) => row.id}
                            disableRowSelectionOnClick
                            rowSelectionModel={{ type: 'include', ids: new Set(selectedMappingId ? [selectedMappingId] : []) }}
                            onRowClick={(params) => selectMapping(params.row as KotobeeBookCloudIdModel)}
                            pageSizeOptions={[10, 25, 50]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                        page: 0,
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
            </Paper>
            <Snackbar
                open={Boolean(successMessage)}
                autoHideDuration={3000}
                onClose={() => setSuccessMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSuccessMessage(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>
            <Snackbar
                open={Boolean(deleteMessage)}
                autoHideDuration={3500}
                onClose={() => setDeleteMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setDeleteMessage(null)} severity={deleteMessage?.severity || 'info'} variant="filled" sx={{ width: '100%' }}>
                    {deleteMessage?.text}
                </Alert>
            </Snackbar>
            <ConfirmDialog
                open={Boolean(deleteCandidate)}
                title="Delete Kotobee mapping"
                message={deleteCandidate ? `Delete mapping for ${deleteCandidate.bookName}?` : ''}
                confirm={confirmDelete}
                deny={closeDeleteDialog}
                confirmText="Delete"
                denyText="Cancel"
                disableOK={Boolean(deletingId)}
                DialogProps={{ fullWidth: true, maxWidth: 'xs' }}
            />
        </Box>
    );
}